// Componente para renderizar un mensaje individual en el chat (versión web).
// Incluye diferentes tipos de mensajes: texto, ubicación, imagen, eliminado, respuesta.
// Adaptado para web desde amarsi-app (React Native a React DOM).

import React, { useState, useEffect, useRef } from 'react';
import { FiMapPin, FiCheck, FiCheckSquare, FiCornerUpLeft, FiCornerUpRight } from 'react-icons/fi';
import { LuCheckCheck } from "react-icons/lu";
import { defaultImage } from '../../constants/defaultImage';
import { useCachedMessageImage } from '../../hooks/useCachedMessageImage';

// Función de comparación personalizada para React.memo
// Solo re-renderiza si cambian props críticas del mensaje
const arePropsEqual = (prevProps, nextProps) => {
  return (
    prevProps.msg.id === nextProps.msg.id &&
    prevProps.msg.body === nextProps.msg.body &&
    prevProps.msg.is_leido === nextProps.msg.is_leido &&
    prevProps.msg.msg_type === nextProps.msg.msg_type &&
    prevProps.msg.created_at === nextProps.msg.created_at &&
    prevProps.selectionMode === nextProps.selectionMode &&
    JSON.stringify(prevProps.selectedImages) === JSON.stringify(nextProps.selectedImages)
  );
};

function MensajitoComponent({ msg, setRespondTo, focusearInput, markAsRead, selectionMode = false, selectedImages = [], onImageSelect }) {
  // Estado para modal de imagen
  const [showImageModal, setShowImageModal] = useState(false);
  
  // Referencia para evitar múltiples llamadas a markAsRead
  const markedAsReadRef = useRef(false);

  // Determina si el mensaje es del usuario actual
  const soyYo = msg.remitente === msg.current_user_id;

  // Efecto para marcar como leído cuando el mensaje es visible
  useEffect(() => {
    if (!soyYo && !msg.is_leido && !markedAsReadRef.current && markAsRead) {
      markedAsReadRef.current = true;
      markAsRead(msg.id);
    }
  }, [soyYo, msg.is_leido, msg.id, markAsRead]);

  // Estilos para mensajes del destinatario (izquierda)
  const destinatarioStyle = "rounded-xl ml-2 mb-0 pt-1 pr-2.5 pb-1 pl-2 border-none bg-neutral-800 max-w-4/5";

  // Estilos para mensajes del remitente (derecha)
  const remitenteStyle = "rounded-xl mr-2 mb-0 pt-1 pr-2.5 pb-1 pl-2.5 border-none bg-primary-500/30 max-w-4/5";

  // Formatea hora del mensaje
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  };

  // Componente para mensaje eliminado
  const MessageEliminado = () => (
    <p className="text-gray-400 italic">
      {soyYo ? 'Mensaje eliminado' : 'Se eliminó el mensaje'}
    </p>
  );

  // Componente para mensaje de texto simple
  const MessageSimple = ({ labelText }) => (
    <p className="text-gray-300 text-base pb-0.5 break-words">
      {labelText || '...'}
    </p>
  );

  // Componente para mensaje de ubicación
  const MessageLocation = ({ coords }) => {
    let coordinates;
    try {
      coordinates = JSON.parse(coords);
    } catch (e) {
      console.error('Error parsing coordinates:', e);
      return null;
    }

    const openGoogleMaps = () => {
      const lat = coordinates[0];
      const lon = coordinates[1];
      window.open(`https://www.google.com/maps?q=${lat},${lon}`, '_blank');
    };

    return (
      <button
        onClick={openGoogleMaps}
        className="bg-white/10 items-center justify-center p-4 my-0.5 mx-0.5 rounded-xl w-full hover:bg-white/20 transition-colors"
      >
        <FiMapPin size={48} className="text-white/50 mx-auto mb-2" />
        <p className="text-base p-1 text-white opacity-80">
          {soyYo ? 'Ubicación enviada' : 'Ubicación recibida'}
        </p>
      </button>
    );
  };

  // Componente para mensaje de imagen
  const MessageImage = ({ msg }) => {
    const { imageUrl, loading: imageUrlLoading } = useCachedMessageImage(msg.id);

    console.log('--- MessageImage', msg);

    // Skip loading for optimistic messages (temporary IDs)
    const isLoading = msg.is_optimistic ? false : imageUrlLoading;

    const imageId = msg.id_image_ref || msg.id;
    const isSelected = selectedImages.some(img => img.img_id === imageId || img.id === imageId);

    const handleImageClick = () => {
      if (selectionMode && onImageSelect) {
        // En modo de selección, seleccionar/deseleccionar la imagen
        const imageData = {
          img_id: imageId,
          img_url: imageUrl,
          id: imageId
        };
        onImageSelect(imageData);
      } else {
        // En modo normal, abrir el modal de imagen
        setShowImageModal(true);
      }
    };

    if (isLoading) {
      return (
        <div className="rounded-xl w-48 h-48 my-1 bg-gray-200 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-400"></div>
        </div>
      );
    }

    return (
      <>
        {showImageModal && (
          <div
            className="fixed inset-0 bg-black/90 flex items-center justify-center z-50"
            onClick={() => setShowImageModal(false)}
          >
            <img
              src={imageUrl || defaultImage}
              alt="Imagen ampliada"
              className="max-w-[90vw] max-h-[90vh] object-contain"
              onError={(e) => {
                e.target.src = defaultImage;
              }}
            />
          </div>
        )}
        <div className="relative">
          <button
            onClick={handleImageClick}
            className={`rounded-xl w-48 h-48 my-1 overflow-hidden relative ${isSelected ? 'ring-4 ring-primary-500' : ''}`}
          >
            <img
              src={imageUrl || defaultImage}
              alt="Mensaje de imagen"
              className="rounded-xl w-full h-full object-cover"
              onError={(e) => {
                e.target.src = defaultImage;
              }}
            />
            {isSelected && (
              <div className="absolute top-2 right-2 bg-primary-500 rounded-full p-1">
                <FiCheckSquare size={20} className="text-white" />
              </div>
            )}
          </button>
        </div>
      </>
    );
  };

  // Componente para mensaje de respuesta
  // Muestra el mensaje respondido como contexto y el contenido del mensaje actual
  const MessageRespondTo = ({ msg }) => {
    const respondedMessage = msg.msg_extra_data?.responded_to;
    
    return (
      <div>
        {/* Mostrar el mensaje respondido como contexto */}
        {respondedMessage && (
          <div className="border-l-2 border-gray-600 pl-2 mb-1">
            <p className="text-xs text-gray-500 italic">
              Respondiendo a:
            </p>
            <p className="text-sm text-gray-400">
              {respondedMessage.body || 'Mensaje eliminado'}
            </p>
          </div>
        )}
        {/* Mostrar el contenido del mensaje actual (la respuesta) */}
        <MessageSimple labelText={msg.body} />
      </div>
    );
  };

  // Array de componentes para tipos de mensajes
  // msg_type 4 (respuesta) ahora incluye el contenido del mensaje dentro de MessageRespondTo
  const tipoDeMensaje = [
    <MessageEliminado />, // 0: eliminado
    <MessageSimple labelText={msg.body} />, // 1: texto
    <MessageLocation coords={msg.body} />, // 2: ubicación
    <MessageImage msg={msg} />, // 3: imagen
    <MessageRespondTo msg={msg} />, // 4: respuesta (incluye contexto + contenido)
  ];

  // Renderiza el contenedor del mensaje según si es del usuario actual o del otro.
  // Agrega la clase 'group' para habilitar hover en los hijos (iconos de respuesta).
  function defineInterlocutor(soyYo){
    if(soyYo){
      return (

        <div className="group w-full pt-2 flex flex-col justify-end">
          <div className="flex items-center justify-end">

            <div className="p-2 cursor-pointer opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-200" onClick={() => setRespondTo(msg)}>
              <FiCornerUpRight size={16} className="text-neutral-400" />
            </div>
            <div className={remitenteStyle}>
              {tipoDeMensaje[msg.msg_type]}
            </div>

          </div>

          <div className="flex justify-between items-center px-2 py-0">
            <div />
            <div className="pt-1 flex items-center gap-2">
              <span className="text-sm text-gray-400">
                {formatTime(msg.created_at)}
              </span>

              {msg.is_leido ? (
                <div className="text-primary-500">
                  <LuCheckCheck size={14} />
                </div>
              ) : (
                <FiCheck size={14} className="text-gray-400" />
              )}
            </div>
          </div>
        </div>

      );
    }
    return (

        <div className="group w-full pt-2">
          <div className="flex items-center ">

            <div className={destinatarioStyle}>
              {tipoDeMensaje[msg.msg_type]}
            </div>

            <div className="p-2 cursor-pointer opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-200" onClick={() => setRespondTo(msg)}>
              <FiCornerUpLeft size={16} className="text-neutral-400" />
            </div>
          </div>

          <div className="pl-3">
            <span className="text-sm text-gray-400">
              {formatTime(msg.created_at)}
            </span>
          </div>
        </div>

    );
  }

  return defineInterlocutor(soyYo);
}

// Exportar componente optimizado con React.memo
const Mensajito = React.memo(MensajitoComponent, arePropsEqual);

export default Mensajito;
